import { createSlice, createEntityAdapter } from '@reduxjs/toolkit'
import actionFactory from 'Shared/hooks/actionFactory'
import { actions as localActionsF } from './local'
import orderActions from 'FlowTaker/lib/orderActions'

const adapter = createEntityAdapter()

const reducer = createSlice({
  name: 'actions',
  initialState: adapter.getInitialState(),
  reducers: {
    upsert: adapter.upsertOne,
    add: adapter.addOne,
    set: adapter.setOne,
    setAll: adapter.setAll,
    remove: adapter.removeOne,
  },
}).reducer

const actions = (dis, store, restClient) => {
  const localActions = localActionsF(dis, store, restClient)
  const factoryActions = actionFactory(dis, store, 'action', {}, {}, {
    afterChange: () => { localActions.setDirty(true) },
  })

  function persist(actionId) {
    const flowId = store.getState().builder.flow.id
    const action = store.getState().actions.entities[actionId]

    return restClient.put(`/api/flows/${flowId}/actions/${actionId}`, { flowAction: action })
      .then(({ data: action }) => {
        dis({ type: 'actions/set', payload: action })
      })
  }

  function add(data) {
    const actions = _.values(store.getState().actions.entities)
    const orderedActions = orderActions(actions)
    const lastAction = orderedActions[orderedActions.length - 1]

    // add at the last
    const id = uuidv4()
    const action = { id, ...data }

    if (data.kind === 'donate') {
      action.config = { 
        // Keep these defaults in sync with app/lib/flows/templates/donate.rb
        amounts: [100, 300, 500, 1000, 2000, 5000], 
        defaultAmount: 300,
        monthlyUpsellButton: "Donate {{amount}}/month",
        oneOffButton: "Keep my one-off {{amount}} gift",
        coverFeeText: 'Cover the <b>{{fee}}</b> in processing and platform fees',
        monthlyUpsellText: 'Will you consider becoming one of our valued monthly supporters by converting your <b>{{amount}}</b> contribution into a monthly donation?</br></br>Ongoing monthly donations allow us to better focus on our mission.',
        thankYouEnabled: false,
        thankYouBody: "<p>Dear {{ first_name }}</p><p>Thank you for your donation of {{ amount }}</p>",
      }
    }

    if (data.kind === 'custom') {
      action.config = { content: '' }
    }

    dis({ type: 'actions/upsert', payload: action })

    // update previous last action to point to this one
    if (lastAction) {
      dis({ type: 'actions/upsert', payload: { ...lastAction, nextActionId: id } })
    }

    localActions.setDirty(true)
    dis({ type: 'local/upsert', payload: { selectedActionId: id } })
  }

  function markInactive(actionId) {
    const action = store.getState().actions.entities[actionId]
    const actions = _.values(store.getState().actions.entities).filter(a => a.active)
    const orderedActions = orderActions(actions)

    dis({ type: 'actions/upsert', payload: { ...action, active: false } })

    // find any actions whose next action is this action, and update them to the next Action of this one
    const previousActions = orderedActions.filter(a => a.nextActionId === actionId)
    previousActions.forEach(a => {
      dis({ type: 'actions/upsert', payload: { ...a, nextActionId: action.nextActionId } })
    })
    
    let firstActionId = actions.find(a => a.isStart && a.active)?.id
    // If this is the first action, we need to update the next action to now be the first
    if (action.isStart && action.nextActionId) {
      const nextAction = orderedActions.find(a => a.id === action.nextActionId)
      dis({ type: 'actions/upsert', payload: { ...nextAction, isStart: true } })
      firstActionId = nextAction.id
    }

    localActions.setDirty(true)
    dis({ type: 'local/upsert', payload: { selectedActionId: firstActionId } })
  }
  
  function moveAction(actionId, newIndex) {
    const action = store.getState().actions.entities[actionId]
    let order = orderActions(_.values(store.getState().actions.entities))
    
    let actionIndex = order.findIndex(a => a.id === actionId)
    order.splice(newIndex, 0, order.splice(actionIndex, 1)[0]) //https://stackoverflow.com/a/2440723
    
    
    order = order.map((a, i) => {
      let payload = { ...a, isStart: false }
      if (i == 0) {
        payload.isStart = true
      }
      if (i == order.length - 1) {
        payload.nextActionId = null
      } else {
        payload.nextActionId = order[i + 1].id
      }
      return payload
    })
    dis({ type: 'actions/setAll', payload: order })
  }

  function loadAvailable() {
    return restClient.get('/api/flows/available_actions')
      .then(({ data }) => {
        return data
      })
  }

  return {
    persist,
    ...factoryActions,
    add,
    markInactive,
    moveAction,
    loadAvailable,
  }
}

export { reducer, actions }
export default { reducer, actions }
